<template>
    <div class="experience-container" id="experience">
        <div class="title" v-gsap-fade-up="{ delay: 0.1 }">{{ $t("experience.title") }}</div>
        <div class="content">
            <ExperienceCard
              v-for="(card, index) in cards"
              :key="index"
              :ref="el => setCardRef(el, index)"
              v-gsap-fade-up="{ offset: 30, duration: 0.8, delay: index * 0.08 }"
              :header="$t(`experience.cards.${card.key}.header`)"
              :description="$t(`experience.cards.${card.key}.description`)"
              :imageSrc="card.imageSrc"
              :opacity="cardOpacities[index] || 0.1"
            />
        </div>
    </div>
  </template>
  
  <script>
  import ExperienceCard from '../elements/ExperienceCard.vue';
  export default {
    name: "ExperienceBlock",
    components: {
      ExperienceCard
    },
    data() {
      return {
        cardOpacities: [],
        cardRefs: [],
        cards: [
          {
            key: "blockchain",
            imageSrc: "@/assets/crystal.gif"
          },
          {
            key: "uxui",
            imageSrc: "@/assets/torus.gif"
          },
          {
            key: "mobile",
            imageSrc: "@/assets/cube.gif"
          },
          {
            key: "qa",
            imageSrc: "@/assets/romb.gif"
          },
          {
            key: "consulting",
            imageSrc: "@/assets/blob.gif"
          },
          {
            key: "data",
            imageSrc: "@/assets/sphere.gif"
          }
        ]
      };
    },
    mounted() {
      this.$nextTick(() => {
        // Инициализируем массив opacity для всех карточек
        this.cardOpacities = new Array(this.cards.length).fill(0.1);
        this.handleScroll();
      });
      window.addEventListener('scroll', this.handleScroll);
      window.addEventListener('resize', this.handleScroll);
    },
    beforeUnmount() {
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleScroll);
    },
    methods: {
      setCardRef(el, index) {
        if (el) {
          this.cardRefs[index] = el.$el || el;
        }
      },
      handleScroll() {
        const windowHeight = window.innerHeight;
        const centerY = windowHeight / 2;
        
        // Зона видимости - половина высоты экрана от центра
        const visibleZone = windowHeight * 0.5;
        
        this.cardRefs.forEach((cardEl, index) => {
          if (!cardEl) return;
          
          const rect = cardEl.getBoundingClientRect();
          const cardCenterY = rect.top + rect.height / 2;
          
          let opacity;
          
          // Если карточка выше центра или в центре - opacity = 1.0
          if (cardCenterY <= centerY) {
            opacity = 1.0;
          } else {
            // Если карточка ниже центра - увеличиваем opacity от 0.1 до 1.0
            // Расстояние от центра карточки до центра экрана
            const distance = cardCenterY - centerY;
            
            // Нормализуем расстояние (от 0 до 1)
            const normalizedDistance = Math.min(distance / visibleZone, 1);
            
            // Вычисляем opacity: чем ближе к центру, тем выше (от 0.1 до 1.0)
            opacity = 0.1 + (1 - normalizedDistance) * 0.9;
          }
          
          this.cardOpacities[index] = opacity;
        });
      }
    }
  };
  </script>
  
  <style scoped>
  .experience-container {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 5vw;
    min-height: 100vh;
    position: relative;
  }

  .title {
    display: flex;
    justify-content: start;
    align-items: center;
    position: sticky;
    top: 128px;
    align-self: flex-start;
    font-size: clamp(24px, 3vw, 48px);
    font-weight: 300;
    text-transform: uppercase;
    white-space: nowrap;
    flex-shrink: 0;
    height: 82px;
  }

  .content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0;
    max-width: 70%;
  }

  .row {
    display: flex;
    align-items: center;
    width: 100%;
    height: 82px;
    white-space: nowrap;
  }

  .row p {
    margin: 0;
    font-size: clamp(22px, 2.86vw, 55px);
    font-weight: 300;
    text-transform: uppercase;
    line-height: 0.8;
    color: #fff;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .graphic-smile {
    flex: 1;
    min-width: clamp(40px, 5vw, 80px);
    width: 100%;
    height: auto;
    object-fit: fill;
    margin: 0 clamp(12px, 1.5vw, 24px);
  }

  .graphic-butterfly {
    flex: 1;
    min-width: clamp(60px, 8vw, 120px);
    width: 100%;
    height: auto;
    object-fit: fill;
    margin-left: clamp(12px, 1.5vw, 24px);
  }

  .graphic-waves {
    flex: 1;
    min-width: clamp(80px, 10vw, 160px);
    width: 100%;
    height: 48px;
    object-fit: fill;
    margin-right: clamp(12px, 1.5vw, 24px);
    border-radius: 1000px;
    background: url("/src/assets/wawes.gif");
  }

  .graphic-waves-small {
    flex: 1;
    min-width: clamp(40px, 5vw, 80px);
    width: 100%;
    height: 48px;
    object-fit: fill;
    border-radius: 1000px;
    margin-left: clamp(12px, 1.5vw, 24px);
    background: url("/src/assets/wawes2.gif");
  }

  .stats-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 1fr;
    width: 100%;
    gap: clamp(16px, 2vw, 32px);
    margin-top: clamp(32px, 4vw, 64px);
    position: relative;
  }

  .stats-grid::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      to right,
      #ffffff50 0%,
      #ffffff50 calc(50% - clamp(8px, 1vw, 16px)),
      transparent calc(50% - clamp(8px, 1vw, 16px)),
      transparent calc(50% + clamp(8px, 1vw, 16px)),
      #ffffff50 calc(50% + clamp(8px, 1vw, 16px)),
      #ffffff50 0%
    );
    transform: translateY(-50%);
    z-index: 1;
  }

  .stats-grid::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 0;
    bottom: 0;
    width: 1px;
    background: linear-gradient(
      to bottom,
      #ffffff50 0%,
      #ffffff50 calc(50% - clamp(8px, 1vw, 16px)),
      transparent calc(50% - clamp(8px, 1vw, 16px)),
      transparent calc(50% + clamp(8px, 1vw, 16px)),
      #ffffff50 calc(50% + clamp(8px, 1vw, 16px)),
      #ffffff50 100%
    );
    transform: translateX(-50%);
    z-index: 1;
  }

  .stat-item {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: clamp(24px, 3vw, 48px);
    box-sizing: border-box;
    position: relative;
    z-index: 2;
  }

  .stat-number {
    font-size: clamp(48px, 8vw, 120px);
    font-weight: 300;
    color: #fff;
    line-height: 1;
    margin-bottom: clamp(8px, 1vw, 16px);
  }

  .stat-label {
    font-size: clamp(12px, 1.5vw, 18px);
    font-weight: 300;
    text-transform: uppercase;
    color: #fff;
    text-align: center;
    letter-spacing: clamp(1px, 0.2vw, 2px);
  }

  @media screen and (max-width: 768px) {
    .experience-container {
      flex-direction: column;
      gap: 32px;
    }

    .title {
      position: static;
      text-align: center;
      width: 100%;
      display: flex;
      justify-content: center;
    }

    .content {
      max-width: 100%;
    }
  }

  @media screen and (max-width: 1024px) {

  }
  </style>
  